import { Fragment, useCallback, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UserGroupsFilterForm from './UserGroupsFilterForm';
import { Button, Table, Modal, Space, message, Dropdown, Spin, Tooltip, Divider } from 'antd';
import { timeString } from '@/utils/utils';
import { fullPagerConfig } from '@/utils/property';
import { get, put } from '@/core/request';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY, MAIN_TOOLBAR_DEDUCT_SELECTORS } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import PageHeader from '@components/PageHeader/PageHeader';
import './UserGroups.less';
export default function UserGroups() {
    const navigate = useNavigate();
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    // 渲染table数据的state
    const [tableData, setTableData] = useState({});
    const [filterObj, setFilterObj] = useState({});

    // 分页设置
    const [pageSize, setPageSize] = useState(20);
    const [currPage, setCurrPage] = useState(1);

    // 筛选数据
    const handleFilterFinish = value => {
        setFilterObj(value);
        setCurrPage(1);
    };

    // 改变一页有多少数据
    const changePageSize = useCallback(pageSize => {
        setPageSize(pageSize);
    }, []);

    // 改变第几页
    const changeCurrPage = useCallback(page => {
        setCurrPage(page);
    }, []);

    const { name, createStaffPhone, status, createdBeginAt, createdEndAt } = filterObj;

    const [pedding, setPedding] = useState(true);
    const getData = useCallback(() => {
        setPedding(true);
        get(`/user/data/user-group/page`, {
            limit: pageSize,
            page: currPage,
            name,
            createStaffPhone,
            status,
            createdBeginAt,
            createdEndAt,
        }).then(res => {
            if (res.status_code === 200) {
                if (res.data) {
                    res.data.list.forEach(item => {
                        item.key = item.id;
                    });
                }
                setPedding(false);
                setTableData(res.data);
            }
        });
    }, [pageSize, currPage, name, createStaffPhone, status, createdBeginAt, createdEndAt]);

    // 渲染table数据
    useEffect(() => {
        getData();
    }, [getData]);

    // 创建人
    const [createdPhone, setCreatedPhone] = useState([]);
    // 创建人
    useEffect(() => {
        get('/user/data/findAll').then(res => {
            setCreatedPhone(res.data);
        });
    }, []);

    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        pageSize,
        size: 'small',
        total: tableData.totalCount,
        onShowSizeChange: (_current, pageSize) => changePageSize(pageSize),
        onChange: page => {
            changeCurrPage(page);
        },
        current: currPage,
    };

    const [refreshBool, setRefreshBool] = useState(false);
    // 点击刷新
    const handleRefresh = () => {
        message.info('正在刷新', 1);
        setRefreshBool(true);
    };

    useEffect(() => {
        if (refreshBool) {
            changeCurrPage(1);
            setRefreshBool(false);
        }
    }, [refreshBool, changeCurrPage]);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: MAIN_TOOLBAR_DEDUCT_SELECTORS,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    // 点击操作区禁用
    const stopConfirm = row => {
        return () => {
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle"></i>,
                content: '是否要启用/禁用选中标签',
                okText: '确认',
                cancelText: '取消',
                onOk: () => {
                    let status = 1;
                    if (row.status === 1) {
                        status = 0;
                    }
                    put('/user/data/user-group/status', {
                        id: row.id,
                        status,
                        name: row.name,
                    }).then(res => {
                        if (res.status_code === 200) {
                            message.success(res.message);
                            getData();
                        }
                    });
                },
            });
        };
    };

    // 点击新增用户分群
    const handleClickAddUserGroups = () => {
        navigate('/uc/user-groups/new');
    };

    // 点击详情进入用户分群
    const handleClickEditUserGroups = row => {
        return () => {
            navigate(`/uc/user-groups/${row.id}`);
        };
    };

    // 导出数据
    const handleExportFile = row => {
        return () => {
            if (row.number >= 100000) {
                message.info('只能导出10万条数据');
            }
            get(
                `/user/data/user-group/download`,
                {
                    id: row.id,
                },
                { responseType: 'blob' },
            ).then(res => {
                if (res.size !== 0) {
                    const date = new Date();
                    const dateString = timeString(date);
                    message.success('请稍后，正在导出');
                    const fileName = `${row.name}${dateString}.xlsx`;
                    const a = document.createElement('a');
                    const event = new MouseEvent('click');
                    a.download = fileName;
                    const objectUrl = URL.createObjectURL(res);
                    a.href = objectUrl;
                    a.dispatchEvent(event);
                    URL.revokeObjectURL(objectUrl);
                } else {
                    message.warning('当前列表没有数据，无法导出');
                }
            });
        };
    };

    // table 数据相关
    const columns = [
        { title: '分群ID', dataIndex: 'id', key: 'id' },
        {
            title: '分群名称',
            dataIndex: 'name',
            render: (_operates, row) => (
                <Fragment>
                    <div>{row.name}</div>
                </Fragment>
            ),
        },
        {
            title: '用户数',
            dataIndex: 'number',
            width: 100,
        },
        {
            title: '创建人',
            dataIndex: 'createdByStaffName',
            width: 100,
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: operates => <span className={operates === 0 ? 'close-status' : 'open-status'}>{operates === 0 ? '禁用' : '启用'}</span>,
        },
        {
            title: '操作',
            dataIndex: 'operate',
            width: 120,
            render: (_operates, row) => {
                const userGroupMenu = {
                    items: [
                        { key: 'download', label: '导出分群用户' },
                        { key: 'status', label: row.status === 0 ? '启用' : '禁用' },
                    ],
                    onClick: ({ key }) => {
                        if (key === 'download') {
                            handleExportFile(row)();
                        }
                        if (key === 'status') {
                            stopConfirm(row)();
                        }
                    },
                };
                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button size="small" type="link" onClick={handleClickEditUserGroups(row)}>
                            详情
                        </Button>
                        <Dropdown menu={userGroupMenu} placement="bottomRight">
                            <Button size="small" type="link" icon={<i className="bi bi-three-dots" aria-hidden="true" />} />
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];
    return (
        <div className="page-container list-page-container user-groups-page">
            <PageHeader title="用户分群" breadcrumb={[{ label: '用户中心', path: '/uc' }, { label: '用户分群' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <div className="user-groups-filter-panel">
                    <UserGroupsFilterForm handleFilterFinish={handleFilterFinish} createdPhone={createdPhone} />
                </div>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container user-groups-table-region">
                <div className="main-toolbar user-groups-table-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={handleClickAddUserGroups}>
                                新增用户分群
                            </Button>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<i className="bi bi-arrow-clockwise"></i>} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap user-groups-table-wrap">
                    <Spin spinning={pedding} delay={500}>
                        <Table
                            className={tableScrollY != null ? 'custom-table-scrollbar-always enterprise-table' : 'enterprise-table'}
                            columns={columns}
                            dataSource={tableData?.list}
                            scroll={{
                                x: 'max-content',
                                ...(tableScrollY != null ? { y: tableScrollY } : {}),
                            }}
                            sticky
                            size="medium"
                            bordered
                            pagination={paginationProps}
                        />
                    </Spin>
                </div>
            </div>
        </div>
    );
}
